import { DollarSign, TrendingUp, Receipt, Wallet, AlertCircle } from 'lucide-react';
import { MetricCard, MetricComparison } from '@/components/reports/MetricCard';
import { FinanceIncomeSummary } from '@/types/finance';
import { ComparisonData, ComparisonMetrics } from '@/hooks/useComparisonMetrics';
import { calculateChange } from '@/utils/periodComparison';
import { useCompanyTax } from '@/hooks/useCompanyTax';
import { formatTaxLine, formatTaxSuffix } from '@/utils/tax';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

interface IncomeSummaryCardsProps {
  data: FinanceIncomeSummary | null;
  isLoading: boolean;
  comparisonData?: ComparisonData;
  comparisonLoading?: boolean;
}

export const IncomeSummaryCards = ({ data, isLoading, comparisonData, comparisonLoading = false }: IncomeSummaryCardsProps) => {
  const tax = useCompanyTax();
  const { formatMoney: formatCurrency } = useCompanyLocale();
  if (isLoading || !data) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-24 bg-muted animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  const getComparison = (
    current: number,
    previousKey: keyof ComparisonMetrics,
    isPositiveGood = true
  ): MetricComparison | undefined => {
    if (!comparisonData) return undefined;
    const change = calculateChange(current, comparisonData.metrics[previousKey]);
    return { ...change, label: comparisonData.period.label, isPositiveGood };
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
      <MetricCard
        title={tax.enabled ? `Total Revenue (${formatTaxSuffix(tax)})` : 'Total Revenue'}
        value={formatCurrency(data.totalRevenue)}
        icon={DollarSign}
        iconBgColor="bg-success/10"
        iconColor="text-success"
        tooltip={tax.enabled ? `Total revenue including ${tax.label} from completed jobs` : 'Total revenue from completed jobs'}
        comparison={getComparison(data.totalRevenue, 'totalTurnover', true)}
        comparisonLoading={comparisonLoading}
      />
      {tax.enabled && (
        <MetricCard
          title={`${tax.label} Collected`}
          value={formatCurrency(data.gstCollected)}
          icon={Receipt}
          iconBgColor="bg-info/10"
          iconColor="text-info"
          tooltip={`${formatTaxLine(tax)} component to be remitted to the relevant authority`}
          comparison={getComparison(data.gstCollected, 'gstOwed', false)}
          comparisonLoading={comparisonLoading}
        />
      )}
      <MetricCard
        title={tax.enabled ? `Net Revenue (ex. ${tax.label})` : 'Net Revenue'}
        value={formatCurrency(data.netRevenue)}
        icon={TrendingUp}
        iconBgColor="bg-success/10"
        iconColor="text-success"
        tooltip={tax.enabled ? `Revenue after ${tax.label} is removed` : 'Revenue after costs and direct expenses'}
        comparison={getComparison(data.netRevenue, 'totalTurnover', true)}
        comparisonLoading={comparisonLoading}
      />
      <MetricCard
        title="Total Collected"
        value={formatCurrency(data.totalCollected)}
        icon={Wallet}
        iconBgColor="bg-primary/10"
        valueColor="text-success"
        tooltip="Actual payments received (Cash, Card, Bank Transfer)"
      />
      <MetricCard
        title="Outstanding Balance"
        value={formatCurrency(data.outstandingBalance)}
        icon={AlertCircle}
        iconBgColor="bg-warning/10"
        iconColor="text-warning"
        valueColor={data.outstandingBalance > 0 ? "text-warning" : "text-foreground"}
        tooltip="Revenue not yet collected"
      />
    </div>
  );
};
